import {Button, IconButton} from '@wandb/components';
import React, {useState} from 'react';

import {PatchDelta} from '../../components/FileBrowser/PatchDelta';
import {useParsePatch} from '../../components/FileBrowser/PatchViewer/useParsePatch';
import {usePatchStats} from '../../components/FileBrowser/PatchViewer/usePatchStats';
import {findPatchFile} from '../../components/ReproduceRun/utils';
import {useRunOverviewPatchItemQuery} from '../../generated/graphql';
import {useOnPressAnalytics} from '../../services/analytics';
import {DD_PRIVACY_HIDDEN} from '../../util/datadog/privacy';
import {propagateErrorsContext} from '../../util/errors';
import {useLoadFile} from '../../util/requests';
import * as Urls from '../../util/urls';
import {makeDownloadUrl} from '../../util/urls';
import {RunOverviewItem} from './RunOverviewItem';

type RunOverviewPatchItemProps = {
  entityName: string;
  projectName: string;
  runName: string;
};

export function RunOverviewPatchItem({
  entityName,
  projectName,
  runName,
}: RunOverviewPatchItemProps) {
  const {data} = useRunOverviewPatchItemQuery({
    variables: {
      entityName,
      projectName,
      runName,
    },
    context: propagateErrorsContext(),
  });
  const run = data?.project?.run;

  const onViewFile = useOnPressAnalytics('Reproduce run', {
    action: 'view patch',
  });
  const onDownloadPatch = useOnPressAnalytics('Reproduce run', {
    action: 'download patch',
  });
  const [fileBody, setFileBody] = useState<string | null>(null);
  const file = run ? findPatchFile(run.patchFiles) : null;
  const fileLoading = useLoadFile(
    {
      entityName,
      projectName,
      runName,
    },
    file?.name ?? '',
    {
      onSuccess: setFileBody,
      skip: !file,
    }
  );
  const [patch] = useParsePatch(fileBody);
  const patchStats = usePatchStats(patch);
  if (!file) {
    return null;
  }

  const patchViewUrl = Urls.runTabFile(entityName, projectName, runName, [
    file.name,
  ]);

  const stackableControls = (
    <Button variant="ghost" href={patchViewUrl} onPress={onViewFile}>
      View file
    </Button>
  );
  const controls = file.url ? (
    <IconButton
      data-dd-privacy={DD_PRIVACY_HIDDEN}
      variant="ghost"
      icon="download"
      aria-label="Download file"
      href={makeDownloadUrl(file.url, file.name)}
      onPress={onDownloadPatch}
    />
  ) : null;

  return (
    <>
      <RunOverviewItem
        name="Patch file"
        controls={controls}
        stackableControls={stackableControls}
        isCommand>
        <div className="flex flex-row gap-8 items-center">
          <span>{file.name}</span>
          {fileLoading || !patchStats ? null : (
            <div className="flex flex-row gap-4 items-center">
              <PatchDelta
                adds={patchStats.adds}
                dels={patchStats.dels}
                size="sm"
              />
            </div>
          )}
        </div>
      </RunOverviewItem>
    </>
  );
}
